/* ═══════════════════════════════════════════════════════════════
   THE MASTER FORMA STUDIO — ground-up rebuild (docs/DESIGNER_UNIFIED_PLAN.md §3 R6).

   The master forma editor is a LAY PERSON'S tool for setting up a
   client's Word formatting rules. Built entirely from the Designer's
   proven stack — two-row header, §2.8 panel-button system, FormaDock
   workspace, universal undo, the shared body/zone-layout brains — so it
   inherits the same feel and the same guarantee: WYSIWYG IS LAW, the
   paper on screen is exactly what word-engine prints.

   Phase A (this file): the shell + the WYSIWYG canvas. Import, export,
   CSI-defaults UI, EOS controls beyond the basics, and mixed-size zone
   text land in phase B — see the header comment for the full list.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { Check } from 'lucide-react';
import type { StandardConfig } from '../../shared/types';
import { Prefs } from '../services/prefsService';
import { FirmPrefs } from '../services/firmPrefsService';
import { FormaService } from '../services/formaService';
import { HistoryButton } from '../components/EditorComponents';
import EmptyState from '../components/EmptyState';
import type { FormaDockHandle } from '../components/FormaDock';
import { useUndoCoordinator } from '../designer/undoCoordinator';
import { DesignerToolbar, type PanelEntry, type ToolbarItem } from '../designer/Toolbar';
import { UniversalHistory } from '../designer/ToolbarControls';
import { isOverDockZone, type FloatingTool } from '../designer/Workspace';
import { pageVariants, type PageVariant, type StyleSelection } from '../designer/formaZonesModel';
import { fillFormaCsiGaps } from '../../shared/csi-defaults';
import '../designer/Designer.css';
import './Studio.css';
import { STUDIO_PALETTES, STUDIO_PANEL_ICONS, StudioWorkspace } from './StudioWorkspace';
import { StudioCanvas } from './StudioCanvas';
import { useMasterFormaDraft } from './useMasterFormaDraft';
import { AttachChip } from './AttachChip';

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 1.6;
const ZOOM_KEY = 'formaStudio.zoom';

/** Where the firm's default Studio layout lives (contract §2.9). ONE
 *  constant — swapping the out-of-box default is a one-line change. */
export const STUDIO_FIRM_LAYOUT_KEY = 'sf.formaStudioDock.firmDefault.v1';

function IconButton({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      className="dsg-icon-btn"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
    >
      {children}
    </button>
  );
}

function SaveIndicator({ dirty, saving }: { dirty: boolean; saving: boolean }) {
  const label = saving ? 'Saving…' : dirty ? 'Unsaved changes' : 'Saved';
  return (
    <span
      className={`dsg-save dsg-save-${saving ? 'saving' : dirty ? 'dirty' : 'clean'}`}
      role="status"
      aria-live="polite"
    >
      {!saving && !dirty && <Check size={12} aria-hidden="true" />}
      {label}
    </span>
  );
}

export interface StudioProps {
  /** Null only for a brand-new, not-yet-saved forma — pass `newForma`
   *  alongside it (contract §3 R6, item 10a). */
  formaId: string | null;
  /** A CSI-seeded, in-memory config to start from when `formaId` is null. */
  newForma?: StandardConfig | null;
  onBack: () => void;
  organizations: Array<{ id: string; name?: string; shortName?: string }>;
  /** For "Export to project" (contract §3 R6, item 3) — masters only. */
  projects: Array<{ id: string; name?: string; projectNumber?: string }>;
  canSetFirmDefault?: boolean;
  toast: (msg: string, isError?: boolean) => void;
  isDark?: boolean;
  userId?: string;
  userName: string;
  /** Mirrors a save or an "Export to project" copy into the caller's own
   *  forma list — the same optimistic update the classic editor does
   *  locally, so a brand-new forma shows up in the Formas library the
   *  moment you go back, without waiting on the live-sync round trip. */
  onFormaPersisted?: (forma: StandardConfig) => void;
  /** Hands the shell a stable "save now" trigger while the Studio is
   *  mounted (null on unmount) — the native File-menu Ctrl+S accelerator
   *  routes through this, same as the in-app Save button. */
  registerSaveHandler?: (fn: (() => void) | null) => void;
}

export function Studio({
  formaId,
  newForma,
  onBack,
  organizations,
  projects,
  onFormaPersisted,
  registerSaveHandler,
  canSetFirmDefault = false,
  toast,
  isDark = false,
  userId,
  userName,
}: StudioProps) {
  const draft = useMasterFormaDraft(formaId, userName, userId, newForma);
  const forma = draft.forma;

  /* ─── Universal undo (§2.7) — one domain today ('forma'), architected the
     same way the Designer joins two, so phase B's editable surfaces slot in
     without a rewrite. */
  const undoTimeline = useUndoCoordinator();
  React.useEffect(() => {
    undoTimeline.register('forma', {
      undo: () => draft.undo(),
      redo: () => draft.redo(),
    });
    return () => undoTimeline.register('forma', null);
  }, [undoTimeline, draft]);
  const resetTimeline = undoTimeline.reset;
  React.useEffect(() => resetTimeline(), [formaId, resetTimeline]);

  const editNoted = React.useCallback(
    (fn: (f: StandardConfig) => StandardConfig) => {
      draft.edit(fn);
      undoTimeline.note('forma');
    },
    [draft, undoTimeline]
  );

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.altKey) return;
      const target = e.target as HTMLElement | null;
      if (target && /^(input|textarea)$/i.test(target.tagName)) return;
      const key = e.key.toLowerCase();
      if (key === 's') {
        e.preventDefault();
        void handleSaveRef.current();
        return;
      }
      const isUndo = key === 'z' && !e.shiftKey;
      const isRedo = key === 'y' || (key === 'z' && e.shiftKey);
      if (!isUndo && !isRedo) return;
      e.preventDefault();
      if (isUndo) undoTimeline.undo();
      else undoTimeline.redo();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [undoTimeline]);

  const handleSave = React.useCallback(async () => {
    if (!forma?.docSettings?.standardName?.trim()) {
      toast('Enter a forma name first', true);
      return;
    }
    try {
      const saved = await draft.save();
      if (saved) onFormaPersisted?.(saved);
      toast('Saved');
    } catch (err) {
      toast(
        'Save failed: ' + (err instanceof Error ? err.message : String(err ?? 'unknown')),
        true
      );
    }
  }, [draft, forma, onFormaPersisted, toast]);
  const handleSaveRef = React.useRef(handleSave);
  handleSaveRef.current = handleSave;
  React.useEffect(() => {
    registerSaveHandler?.(() => handleSaveRef.current());
    return () => registerSaveHandler?.(null);
  }, [registerSaveHandler]);

  /* ─── Import as draft (contract §3 R6, item 1) — same IPC as the classic
     editor's handleImport (app.tsx). CSI gap-fill runs on the harvest so an
     import that left spacing/indents unsaid starts CSI-correct instead of
     0 (item 10b) — imported values always win where present. */
  const handleImport = React.useCallback(async () => {
    if (!window.api?.openDotxDialog || !window.api?.importTemplate) {
      toast('Import requires the desktop app', true);
      return;
    }
    const filePath = await window.api.openDotxDialog();
    if (!filePath) return;
    try {
      const result = await window.api.importTemplate(filePath);
      if (!result.success || !result.config) {
        toast('Import failed: ' + (result.error || 'Unknown error'), true);
        return;
      }
      draft.importDraft(fillFormaCsiGaps(result.config));
      undoTimeline.reset();
      toast('Imported — review and Save to add it to your Master Formas');
    } catch (err) {
      toast('Import error: ' + (err instanceof Error ? err.message : String(err)), true);
    }
  }, [draft, toast, undoTimeline]);

  /* ─── Export .dotx (round-trip) — same shape the classic editor's
     handleExport builds (app.tsx ~1929). ─── */
  const handleExportDotx = React.useCallback(async () => {
    if (!forma || !window.api?.saveDotxDialog || !window.api?.createTemplate) {
      toast('Export requires the desktop app', true);
      return;
    }
    const name =
      (forma.docSettings?.standardName || 'template').replace(/[^a-zA-Z0-9_-]/g, '_') + '.dotx';
    const savePath = await window.api.saveDotxDialog(name);
    if (!savePath) return;
    try {
      const cfg: StandardConfig = {
        ...forma,
        images: forma.images && Object.keys(forma.images).length > 0 ? forma.images : undefined,
        placeholders:
          forma.placeholders && Object.keys(forma.placeholders).length > 0
            ? forma.placeholders
            : undefined,
        bindings:
          forma.bindings && Object.keys(forma.bindings).length > 0 ? forma.bindings : undefined,
      };
      const result = await window.api.createTemplate(cfg, savePath);
      if (result.success) toast('Template exported');
      else toast('Export failed', true);
    } catch (err) {
      toast('Export error: ' + (err instanceof Error ? err.message : String(err)), true);
    }
  }, [forma, toast]);

  /* ─── Export to project (contract §3 R6, item 3) — same copy-on-write
     model as the classic editor's "Export to project" (app.tsx ~2843):
     the project gets its OWN copy; this master stays untouched. Saves
     first if there are unsaved edits — the copy is made from the SAVED
     master, never a draft. ─── */
  const handleExportToProject = React.useCallback(
    async (projectId: string) => {
      if (!projectId || !forma) return;
      const project = projects.find((p) => p.id === projectId);
      try {
        if (draft.dirty) await handleSave();
        const master = draft.forma;
        if (!master?.id) {
          toast('Save the forma first, then export it to a project', true);
          return;
        }
        const copy = await FormaService.ensureProjectCopy(master, {
          id: projectId,
          number: project?.projectNumber,
          name: project?.name,
        });
        onFormaPersisted?.(copy);
        toast(`Exported to ${project?.name || 'project'} — the project now has its own copy`);
      } catch (err) {
        toast('Export failed: ' + (err instanceof Error ? err.message : String(err)), true);
      }
    },
    [draft, forma, handleSave, onFormaPersisted, projects, toast]
  );

  const leave = React.useCallback(() => {
    if (draft.dirty && !confirm('Discard unsaved changes?')) return;
    onBack();
  }, [draft.dirty, onBack]);

  /* ─── §2.8 panel buttons: click = anchored dropdown, drag = dock/float ─── */
  const dockHandleRef = React.useRef<FormaDockHandle | null>(null);
  const [dockPanels, setDockPanels] = React.useState<string[]>([]);
  const [floatingTools, setFloatingTools] = React.useState<FloatingTool[]>([]);
  const [dropdown, setDropdown] = React.useState<{
    id: string;
    anchor: { left: number; bottom: number };
  } | null>(null);
  const [panelDragId, setPanelDragId] = React.useState<string | null>(null);

  React.useEffect(() => {
    setFloatingTools((f) => {
      const next = f.filter((t) => !dockPanels.includes(t.id));
      return next.length === f.length ? f : next;
    });
    setDropdown((d) => (d && dockPanels.includes(d.id) ? null : d));
  }, [dockPanels]);

  const floatTool = React.useCallback((id: string, at?: { left: number; top: number } | null) => {
    setDropdown((d) => (d?.id === id ? null : d));
    setFloatingTools((f) => (f.some((t) => t.id === id) ? f : [...f, { id, at }]));
  }, []);

  const openTool = React.useCallback(
    (id: string) => {
      if (dockPanels.includes(id)) {
        dockHandleRef.current?.showPanel(id);
        return;
      }
      floatTool(id);
    },
    [dockPanels, floatTool]
  );

  const closeFloating = React.useCallback(
    (id: string) => setFloatingTools((f) => f.filter((t) => t.id !== id)),
    []
  );
  const dockFloating = React.useCallback((id: string) => {
    setFloatingTools((f) => f.filter((t) => t.id !== id));
    dockHandleRef.current?.showPanel(id);
  }, []);

  const onPanelClick = React.useCallback(
    (id: string, anchor: { left: number; bottom: number }) => {
      if (dockPanels.includes(id)) {
        dockHandleRef.current?.showPanel(id);
        return;
      }
      if (floatingTools.some((t) => t.id === id)) {
        closeFloating(id);
        return;
      }
      setDropdown((d) => (d?.id === id ? null : { id, anchor }));
    },
    [dockPanels, floatingTools, closeFloating]
  );

  /** Open a panel by id from somewhere other than its toolbar button — the
   *  paper's end-of-section line opens "Section End" this way, restoring the
   *  classic canvas's onEditSpecial('{EndOfSection}') shortcut. Anchors on
   *  the real button so the dropdown lands where the user expects it. */
  const openPanelById = React.useCallback(
    (id: string) => {
      const btn = document.querySelector<HTMLElement>(`.dsg-panel-btn[data-panel-id="${id}"]`);
      const r = btn?.getBoundingClientRect();
      onPanelClick(id, r ? { left: r.left, bottom: r.bottom } : { left: 24, bottom: 96 });
    },
    [onPanelClick]
  );

  const onPanelDragEnd = React.useCallback(
    (id: string, x: number, y: number) => {
      setPanelDragId(null);
      if (isOverDockZone(x, y)) return;
      floatTool(id, { left: x, top: y });
    },
    [floatTool]
  );

  const undockToFloat = React.useCallback(
    (id: string, at: { left: number; top: number }) => {
      dockHandleRef.current?.hidePanel(id);
      floatTool(id, at);
    },
    [floatTool]
  );

  const setFirmDefault = React.useCallback(() => {
    const layout = dockHandleRef.current?.snapshotLayout();
    if (!layout) {
      toast('The workspace is still opening — try again in a moment.', true);
      return;
    }
    FirmPrefs.set(STUDIO_FIRM_LAYOUT_KEY, layout, userName)
      .then(() =>
        toast('Saved as the firm default — everyone who has not customised will see this layout')
      )
      .catch((err: unknown) =>
        toast(
          'Could not save the firm default: ' +
            (err instanceof Error ? err.message : String(err ?? 'unknown')),
          true
        )
      );
  }, [toast, userName]);

  const panelEntries = React.useMemo<PanelEntry[]>(
    () =>
      STUDIO_PALETTES.filter((p) => p.id !== 'canvas').map((p) => ({
        id: p.id,
        title: p.title,
        icon: STUDIO_PANEL_ICONS[p.id] ?? null,
        where: dockPanels.includes(p.id)
          ? 'docked'
          : floatingTools.some((t) => t.id === p.id)
            ? 'floating'
            : dropdown?.id === p.id
              ? 'dropdown'
              : null,
      })),
    [dockPanels, floatingTools, dropdown]
  );

  // ── Zone selection + level focus (ONE selection system, DESIGN §3.4) ──
  // Order 1 (Josh, 2026-08-13): the "Header & Footer" panel is gone —
  // selecting a zone band just marks it selected on the sheet; no panel to
  // open.
  const [styleSel, setStyleSel] = React.useState<StyleSelection>(null);
  const onStyleSelect = React.useCallback((sel: StyleSelection) => {
    setStyleSel(sel);
  }, []);
  const [levelFocus, setLevelFocus] = React.useState<number | null>(null);
  const openLevelStyle = React.useCallback(
    (level: number) => {
      setLevelFocus(level);
      openTool('levels');
    },
    [openTool]
  );

  const [pageVariant, setPageVariant] = React.useState<PageVariant>('odd');
  const pvs = pageVariants(forma);
  const effPageVariant = pvs.includes(pageVariant) ? pageVariant : 'odd';

  const [tokenDrag, setTokenDrag] = React.useState(false);
  const [dockLocked, setDockLocked] = React.useState<boolean>(() => {
    try {
      return localStorage.getItem('sf.formaStudioDockLocked') !== '0';
    } catch {
      return true;
    }
  });
  const toggleDockLocked = React.useCallback(
    () =>
      setDockLocked((v) => {
        const nv = !v;
        try {
          localStorage.setItem('sf.formaStudioDockLocked', nv ? '1' : '0');
        } catch {
          /* private mode */
        }
        return nv;
      }),
    []
  );

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setStyleSel(null);
      setLevelFocus(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Zoom persistence (Josh, 2026-08-13: "isn't saving persistently").
  // `Prefs` is a server-backed map that hydrates ASYNCHRONOUSLY after sign-in
  // and saves on an 800ms debounce, so reading it first meant a stale (or
  // not-yet-loaded) server value beat the zoom you just set on this machine.
  // The LOCAL value is the newest thing this user did here, so it wins; the
  // server copy is only the fallback for a machine that has none yet.
  const validZoom = (v: unknown): number | null => {
    const n = Number(v);
    return Number.isFinite(n) && n >= ZOOM_MIN && n <= ZOOM_MAX ? n : null;
  };
  const [zoom, setZoom] = React.useState<number>(() => {
    let local: string | null = null;
    try {
      local = localStorage.getItem(ZOOM_KEY);
    } catch {
      /* private mode */
    }
    return validZoom(local) ?? validZoom(Prefs.get(ZOOM_KEY)) ?? 1;
  });

  // A machine with no local zoom adopts the synced one the moment prefs land.
  React.useEffect(() => {
    let hasLocal = false;
    try {
      hasLocal = validZoom(localStorage.getItem(ZOOM_KEY)) != null;
    } catch {
      /* private mode */
    }
    if (hasLocal) return;
    return Prefs.subscribe(() => {
      const remote = validZoom(Prefs.get(ZOOM_KEY));
      if (remote != null) setZoom(remote);
    });
  }, []);
  const setZoomPersisted = (next: number) => {
    const clamped = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, Number(next.toFixed(2))));
    setZoom(clamped);
    Prefs.set(ZOOM_KEY, clamped);
    try {
      localStorage.setItem(ZOOM_KEY, String(clamped));
    } catch {
      /* private mode */
    }
  };

  const [renaming, setRenaming] = React.useState(false);
  const [nameDraft, setNameDraft] = React.useState('');

  const clientOptions = React.useMemo(
    () =>
      [
        ...new Set(organizations.map((o) => o.name || o.shortName).filter((n): n is string => !!n)),
      ].map((n) => ({
        value: n,
        label: n,
      })),
    [organizations]
  );

  const projectOptions = React.useMemo(
    () =>
      projects
        .filter((p) => p.name)
        .sort((a, b) => String(a.name).localeCompare(String(b.name)))
        .map((p) => ({
          value: p.id,
          label: p.projectNumber ? `${p.projectNumber} — ${p.name}` : p.name || p.id,
        })),
    [projects]
  );

  const toolbarItems = React.useMemo<ToolbarItem[]>(() => {
    if (!forma) return [];
    return [
      {
        id: 'undo-redo',
        label: 'Undo and redo',
        core: true,
        node: <UniversalHistory timeline={undoTimeline} editor={null} />,
      },
      {
        id: 'zoom',
        label: 'Zoom',
        core: true,
        node: (
          <div className="dsg-group" role="group" aria-label="Zoom">
            <IconButton
              label="Zoom out"
              onClick={() => setZoomPersisted(zoom - 0.1)}
              disabled={zoom <= ZOOM_MIN}
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <circle cx="11" cy="11" r="7" />
                <path d="M8 11h6M20 20l-3.5-3.5" />
              </svg>
            </IconButton>
            <button
              type="button"
              className="dsg-zoom-value"
              onClick={() => setZoomPersisted(1)}
              title="Reset zoom to 100%"
            >
              {Math.round(zoom * 100)}%
            </button>
            <IconButton
              label="Zoom in"
              onClick={() => setZoomPersisted(zoom + 0.1)}
              disabled={zoom >= ZOOM_MAX}
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <circle cx="11" cy="11" r="7" />
                <path d="M8 11h6M11 8v6M20 20l-3.5-3.5" />
              </svg>
            </IconButton>
          </div>
        ),
      },
      {
        id: 'import',
        label: 'Import a .dotx as a draft',
        node: (
          <button
            type="button"
            className="dsg-btn"
            onClick={() => void handleImport()}
            title="Import a Word template as a new, unsaved draft"
          >
            Import…
          </button>
        ),
      },
      {
        id: 'export',
        label: 'Export .dotx',
        node: (
          <button
            type="button"
            className="dsg-btn"
            onClick={() => void handleExportDotx()}
            title="Export this forma as a Word template (.dotx)"
          >
            Export .dotx
          </button>
        ),
      },
      {
        // Placed last before Save — same relative order as the classic
        // toolbar (HistoryButton, Export, Save). Its dropdown anchors
        // `right: 0` off ITS OWN box (EditorComponents.tsx, shared with the
        // frozen classic editor — not portalled/viewport-clamped); parked
        // near the toolbar's right edge is where that math stays on-screen.
        id: 'history',
        label: 'Edit history',
        node: <HistoryButton entries={forma.editHistory || []} />,
      },
      {
        id: 'save',
        label: 'Save now',
        core: true,
        node: (
          <button
            type="button"
            className="dsg-primary"
            onClick={() => void handleSave()}
            disabled={draft.saving}
          >
            Save
          </button>
        ),
      },
    ];
  }, [forma, undoTimeline, zoom, draft.saving, handleSave, handleImport, handleExportDotx]);

  if (draft.loadState === 'loading') {
    return (
      <div className="dsg-root dsg-loading sfs-state-root" aria-busy="true">
        <span className="sr-only">Opening the forma…</span>
        <div className="dsg-skeleton-sheet" />
      </div>
    );
  }

  if (draft.loadState === 'not-found' || draft.loadState === 'error' || !forma) {
    const notFound = draft.loadState === 'not-found';
    return (
      <div className="dsg-root dsg-error-root sfs-state-root">
        <EmptyState
          title={notFound ? 'This forma could not be found' : 'This forma could not be opened'}
          subtitle={
            notFound ? 'It may have been deleted.' : draft.error || 'Try again in a moment.'
          }
          action={
            notFound
              ? { label: 'Back to Formas', onClick: onBack }
              : { label: 'Try again', onClick: draft.reload }
          }
        />
      </div>
    );
  }

  return (
    <div className="dsg-root sfs-root">
      <header className="dsg-topbar">
        <div className="dsg-idrow">
          <button type="button" className="dsg-back" onClick={leave}>
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            Formas
          </button>
          <nav className="dsg-crumb" aria-label="Breadcrumb">
            <span className="dsg-crumb-project">Formas</span>
            <span className="dsg-crumb-sep" aria-hidden="true">
              ▸
            </span>
            {renaming ? (
              <input
                autoFocus
                className="sfs-rename-input"
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                onBlur={() => {
                  setRenaming(false);
                  const v = nameDraft.trim();
                  if (v)
                    editNoted((f) => ({
                      ...f,
                      docSettings: { ...f.docSettings, standardName: v },
                    }));
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                  if (e.key === 'Escape') setRenaming(false);
                }}
              />
            ) : (
              <button
                type="button"
                className="dsg-crumb-spec"
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'text',
                  padding: 0,
                  font: 'inherit',
                  color: 'inherit',
                }}
                onClick={() => {
                  setNameDraft(forma.docSettings?.standardName || '');
                  setRenaming(true);
                }}
                title="Rename this forma"
              >
                <strong>{forma.docSettings?.standardName || 'Untitled forma'}</strong>
              </button>
            )}
          </nav>
          <AttachChip
            value={forma.docSettings?.clientName || ''}
            options={clientOptions}
            placeholder="Search clients…"
            emptyLabel="Add client"
            title="Attach this forma to a client (optional)"
            onChange={(v) => {
              const org = organizations.find((o) => (o.name || o.shortName) === v);
              editNoted((f) => ({
                ...f,
                docSettings: { ...f.docSettings, clientName: v },
                clientId: org?.id,
              }));
            }}
          />
          {/* Export INTO a project — masters only (contract §3 R6, item 3).
             Same model as the classic editor: the project gets its own
             COPY; this master stays untouched. */}
          {!forma.projectId && (
            <AttachChip
              value=""
              options={projectOptions}
              placeholder="Search projects…"
              emptyLabel="Export to project"
              title="Copy this master forma into a project — the project gets its own separate forma; the master stays untouched"
              onChange={(v) => void handleExportToProject(v)}
            />
          )}
          <div className="dsg-idrow-status">
            <SaveIndicator dirty={draft.dirty} saving={draft.saving} />
          </div>
        </div>

        <div>
          <DesignerToolbar
            items={toolbarItems}
            panels={panelEntries}
            onPanelClick={onPanelClick}
            onPanelDragStart={setPanelDragId}
            onPanelDragEnd={onPanelDragEnd}
            dockLocked={dockLocked}
            onToggleDockLocked={toggleDockLocked}
            onResetLayout={() => dockHandleRef.current?.resetLayout()}
            onSetFirmDefault={setFirmDefault}
            canSetFirmDefault={canSetFirmDefault}
            userId={userId}
            storageKeyPrefix="sf.formaStudioToolbar.v1."
          />
        </div>
      </header>

      <div className="dsg-body">
        <main className="dsg-main">
          <StudioWorkspace
            isDark={isDark}
            userId={userId || 'anon'}
            locked={dockLocked}
            forma={forma}
            edit={editNoted}
            canEdit
            toast={toast}
            styleSel={styleSel}
            onStyleSelect={onStyleSelect}
            levelFocus={levelFocus}
            onLevelFocus={setLevelFocus}
            pageVariant={effPageVariant}
            onPageVariant={setPageVariant}
            onDragState={setTokenDrag}
            dragActive={tokenDrag}
            floatingTools={floatingTools}
            onCloseFloating={closeFloating}
            onDockFloating={dockFloating}
            dropdown={dropdown}
            onCloseDropdown={() => setDropdown(null)}
            onFloatDropdown={floatTool}
            onUndock={undockToFloat}
            panelDragId={panelDragId}
            firmLayoutKey={STUDIO_FIRM_LAYOUT_KEY}
            onDockPanelsChange={setDockPanels}
            onOpenPanel={openPanelById}
            onDockReady={(h) => {
              dockHandleRef.current = h;
            }}
            canvas={(wiring) => (
              <StudioCanvas
                forma={forma}
                zoom={zoom}
                design={wiring}
                levelFocus={levelFocus}
                onLevelClick={openLevelStyle}
                edit={editNoted}
                onZoomDelta={(step) => setZoomPersisted(zoom + step)}
                onClearLevel={() => setLevelFocus(null)}
              />
            )}
          />
        </main>
      </div>
    </div>
  );
}

export default Studio;
